'use client';

import { useState } from 'react';
import { useAppStore } from '@/lib/stores/app-store';
import * as Tabs from '@radix-ui/react-tabs';
import { Map, Route, Trophy, ChevronRight, ChevronDown, Lock, Check, Circle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface MapPanelProps {
  showTabs?: boolean;
  onSelectNode?: (nodeId: string | null) => void;
  selectedNodeId?: string | null;
}

export function MapPanel({ showTabs = true, onSelectNode, selectedNodeId }: MapPanelProps) {
  const { language, subjectMap, trail, profile } = useAppStore();
  const [activeTab, setActiveTab] = useState('map');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const isHebrew = language === 'he';

  const text = {
    en: {
      map: 'Map',
      trail: 'Trail',
      stats: 'Stats',
      prerequisites: 'Prerequisites',
      noMap: 'Generate a map to see topics',
      noTrail: 'Select a topic to see the learning trail',
      xp: 'XP',
      level: 'Level',
      streak: 'Streak',
      days: 'days',
      mastery: 'Mastery',
      topics: 'Topics Explored',
    },
    he: {
      map: 'מפה',
      trail: 'מסלול',
      stats: 'סטטיסטיקות',
      prerequisites: 'דרישות קדם',
      noMap: 'צור מפה כדי לראות נושאים',
      noTrail: 'בחר נושא כדי לראות את מסלול הלמידה',
      xp: 'נקודות',
      level: 'רמה',
      streak: 'רצף',
      days: 'ימים',
      mastery: 'שליטה',
      topics: 'נושאים שנחקרו',
    },
  };

  const t = text[language];

  const toggleExpand = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const renderMapNode = (nodeId: string, depth: number = 0) => {
    if (!subjectMap) return null;
    const node = subjectMap.nodes[nodeId];
    if (!node) return null;

    const hasChildren = node.children.length > 0;
    const isExpanded = expandedNodes.has(nodeId);
    const isSelected = selectedNodeId === nodeId;
    const title = isHebrew ? node.titleHe || node.title : node.title;

    const statusIcon = {
      locked: <Lock className="w-4 h-4 text-muted-foreground" />,
      available: <Circle className="w-4 h-4 text-primary" />,
      'in-progress': <Circle className="w-4 h-4 text-warning fill-warning/30" />,
      completed: <Check className="w-4 h-4 text-success" />,
    }[node.status];

    return (
      <div key={nodeId}>
        <div
          className={`map-node flex items-center gap-2 ${
            isSelected ? 'selected' : ''
          } ${node.status === 'locked' ? 'locked' : ''}`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => {
            if (node.status !== 'locked' && onSelectNode) {
              onSelectNode(isSelected ? null : nodeId);
            }
          }}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(nodeId);
              }}
              className="p-1 hover:bg-muted rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          )}
          {!hasChildren && <span className="w-6" />}
          {statusIcon}
          <span className={`flex-1 ${node.isTarget ? 'font-bold text-primary' : ''}`}>
            {title}
          </span>
          {node.isTarget && (
            <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
              Target
            </span>
          )}
        </div>
        <AnimatePresence>
          {hasChildren && isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              {node.children.map((childId) => renderMapNode(childId, depth + 1))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const renderTrail = () => {
    if (!trail) {
      return (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          {t.noTrail}
        </div>
      );
    }

    return (
      <div className="p-4 space-y-4">
        <h3 className="font-semibold text-lg">{trail.topicTitle}</h3>
        <div className="relative">
          {trail.steps.map((step, index) => (
            <div key={step.id} className="flex gap-3 mb-4 relative">
              {/* Line */}
              {index < trail.steps.length - 1 && (
                <div className="trail-line" />
              )}
              {/* Dot */}
              <div
                className={`trail-dot ${
                  step.status === 'completed'
                    ? 'completed'
                    : step.status === 'in-progress'
                    ? 'current'
                    : ''
                }`}
              >
                {step.status === 'completed' && <Check className="w-3 h-3" />}
                {step.status === 'in-progress' && <Circle className="w-3 h-3 fill-current" />}
              </div>
              {/* Content */}
              <div className="flex-1 pb-4">
                <div className="font-medium">
                  {isHebrew ? step.titleHe || step.title : step.title}
                </div>
                <div className="text-sm text-muted-foreground">
                  {step.description}
                </div>
                {step.prerequisites.length > 0 && (
                  <div className="mt-2 text-xs">
                    <span className="text-muted-foreground">{t.prerequisites}: </span>
                    {step.prerequisites.map((p) => (
                      <span
                        key={p.stepId}
                        className={`inline-flex items-center gap-1 mr-2 ${
                          p.status === 'mastered'
                            ? 'text-success'
                            : p.status === 'partial'
                            ? 'text-warning'
                            : 'text-muted-foreground'
                        }`}
                      >
                        {p.status === 'mastered' && <Check className="w-3 h-3" />}
                        {p.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderStats = () => {
    const topicsExplored = Object.keys(profile.topicProgress).length;

    return (
      <div className="p-4 space-y-6">
        {/* XP and Level */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-card border border-border text-center">
            <div className="text-3xl font-bold text-primary">{profile.xp}</div>
            <div className="text-sm text-muted-foreground">{t.xp}</div>
          </div>
          <div className="p-4 rounded-lg bg-card border border-border text-center">
            <div className="text-3xl font-bold">{profile.level}</div>
            <div className="text-sm text-muted-foreground">{t.level}</div>
          </div>
        </div>

        {/* Streak */}
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t.streak}</span>
            <span className="text-xl font-bold">
              {profile.streak} {t.days} 🔥
            </span>
          </div>
        </div>

        {/* Topics */}
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t.topics}</span>
            <span className="text-xl font-bold">{topicsExplored}</span>
          </div>
        </div>

        {/* Achievements placeholder */}
        <div className="p-4 rounded-lg bg-muted/50 border border-dashed border-border text-center text-muted-foreground">
          Achievements coming soon...
        </div>
      </div>
    );
  };

  if (!showTabs) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground p-8">
        <p className="text-center">{t.noMap}</p>
      </div>
    );
  }

  return (
    <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
      <Tabs.List className="flex border-b border-border bg-card">
        <Tabs.Trigger
          value="map"
          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary transition-colors"
        >
          <Map className="w-4 h-4" />
          {t.map}
        </Tabs.Trigger>
        <Tabs.Trigger
          value="trail"
          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary transition-colors"
        >
          <Route className="w-4 h-4" />
          {t.trail}
        </Tabs.Trigger>
        <Tabs.Trigger
          value="stats"
          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary transition-colors"
        >
          <Trophy className="w-4 h-4" />
          {t.stats}
        </Tabs.Trigger>
      </Tabs.List>

      <div className="flex-1 overflow-auto">
        <Tabs.Content value="map" className="h-full">
          {subjectMap ? (
            <div className="p-2">
              {subjectMap.rootIds.map((rootId) => renderMapNode(rootId))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              {t.noMap}
            </div>
          )}
        </Tabs.Content>

        <Tabs.Content value="trail" className="h-full">
          {renderTrail()}
        </Tabs.Content>

        <Tabs.Content value="stats" className="h-full">
          {renderStats()}
        </Tabs.Content>
      </div>
    </Tabs.Root>
  );
}
